import React, { useContext } from 'react';
import RolesContext from '../../contexts/RolesContext';
import { ActionButtons } from '../Shared/ActionButtons';
import { useHistory } from 'react-router-dom';

export interface ISiteMapCard {
  imgSrc: string;
  title: string;
  label: string;
  handleImgClick?: () => void;
  handleDelete?: () => void;
  handleRename?: () => void;
  handleReplace?: () => void;
  handleArchive?: () => void;
  handlePrint?: () => void;
  handleDownload?: () => void;
  canArchive?: boolean;
  canDelete?: boolean;
  canRename?: boolean;
  canReplace?: boolean;
  isArchive?: boolean;
  isLoading?: boolean;
  isDownloaded?: boolean;
  isPending?: boolean;
  siteMapType?: string | null;
}

const SiteMapCard: React.FC<ISiteMapCard> = ({
  imgSrc,
  title,
  label,
  handleImgClick,
  handleDelete,
  handleRename,
  handleReplace,
  handleArchive,
  handlePrint,
  handleDownload,
  canArchive,
  isArchive,
  isLoading,
  isDownloaded,
  isPending,
  siteMapType,
}) => {
  const permCheck = useContext(RolesContext).userHasPermission;
  const canUpdate = permCheck('update', label);
  const canPrint = permCheck('all', 'Download') && navigator.onLine && !isPending;
  const history = useHistory();
  const isPublic = history.location.pathname.startsWith("/public")

  const secondaryActions = [];

  if (canUpdate && !isDownloaded && navigator.onLine && !isPending) {
    secondaryActions.push({
      label: 'Rename',
      icon: 'pencil',
      onClick: handleRename,
    });

    if (siteMapType !== 'gis_only' && siteMapType !== 'gis_esri') {
      secondaryActions.push({
        label: 'Replace',
        icon: 'exchange',
        onClick: handleReplace,
      });
    }

    secondaryActions.push({
      label: 'Delete',
      icon: 'trash',
      onClick: handleDelete,
    });
  } else if (canUpdate && !isDownloaded && navigator.onLine && isPending) {
    // Pending GIS card: only Delete is meaningful before configuration
    secondaryActions.push({
      label: 'Delete',
      icon: 'trash',
      onClick: handleDelete,
    });
  }
  
  if(canPrint){
    secondaryActions.push({
      label: 'Download',
      icon: 'download',
      onClick: handleDownload,
    });
  }

  if (canArchive && !isDownloaded) {
    secondaryActions.push({
      label: isArchive ? 'Restore' : 'Archive',
      icon: isArchive ? 'undo' : 'archive',
      onClick: handleArchive,
    });
  }

  return (
    <section
      className='card'
      style={{
        position: 'relative',
      }}
    >
      {isLoading ? (
        <div className='card-loader'>
          <div className='loader' />
        </div>
      ) : (
        (!isPublic)?
        <ActionButtons
          secondary={secondaryActions}
          className='card-active-site-map-menu-button'
        />
        :
        <></>
      )}
      {isPending ? (
        <div
          className='card-img card-cap'
          style={{
            background: '#f0efe9',
            cursor: handleImgClick && canUpdate ? 'pointer' : 'initial',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#0d2330',
            fontSize: 14,
            textAlign: 'center',
            padding: '20px',
          }}
          title={title}
          onClick={() => (handleImgClick && canUpdate ? handleImgClick() : null)}
        >
          <div>
            <i className='fa fa-map-marker' style={{ fontSize: 32, marginBottom: 8, display: 'block' }} />
            Configure GIS
          </div>
        </div>
      ) : (
        <div
          className='card-img card-cap'
          style={{
            backgroundImage: `url(${imgSrc})`,
            cursor: handleImgClick && canUpdate ? 'pointer' : 'initial',
          }}
          title={title}
          onClick={() => (handleImgClick && canUpdate ? handleImgClick() : null)}
        />
      )}

      <div className='card-body'>
        <p className='card-title'>{title}</p>
      </div>

      <div className='card-body-actions card-actions'>
        <button
          className='card-action pure-button button-primary'
          onClick={handleImgClick}
        >
          {isPending ? 'Configure GIS' : (canUpdate && canArchive ? 'Update Site Map' : 'View Site Map')}
        </button>

        {canPrint && (
          <button
            className='card-action pure-button button-outline-primary '
            onClick={handlePrint}
          >
            Print Site Map
          </button>
        )}
      </div>
    </section>
  );
};

export default SiteMapCard;
